import React from 'react'
import { motion } from 'framer-motion'
import { useApp } from '../contexts/AppContext'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiChevronLeft, FiChevronRight, FiMoreHorizontal } = FiIcons

const Pagination = () => {
  const {
    currentLang,
    currentPage,
    totalPages,
    itemsPerPage,
    setItemsPerPage,
    goToPage,
    goToPreviousPage,
    goToNextPage,
    canGoPrevious,
    canGoNext,
    translations
  } = useApp()

  // Generate page numbers to show
  const getVisiblePages = () => {
    const delta = 1 // Number of pages to show on each side of current page
    const range = []
    const rangeWithDots = []

    // Always include first page
    range.push(1)

    // Add pages around current page
    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i)
    }

    // Always include last page if more than 1 page
    if (totalPages > 1) {
      range.push(totalPages)
    }

    // Remove duplicates and sort
    const uniqueRange = [...new Set(range)].sort((a, b) => a - b)

    // Add dots where there are gaps
    let prev = 0
    for (const page of uniqueRange) {
      if (page - prev > 1) {
        rangeWithDots.push('...')
      }
      rangeWithDots.push(page)
      prev = page
    }

    return rangeWithDots
  }

  const visiblePages = getVisiblePages()

  // Mobile: Show fewer pages
  const getMobileVisiblePages = () => {
    if (totalPages <= 3) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }

    const pages = []
    
    // Always show current page
    pages.push(currentPage)
    
    // Show previous page if exists
    if (currentPage > 1) {
      pages.unshift(currentPage - 1)
    }
    
    // Show next page if exists
    if (currentPage < totalPages) {
      pages.push(currentPage + 1)
    }
    
    // Ensure we have at most 3 pages
    return pages.slice(0, 3)
  }

  const mobileVisiblePages = getMobileVisiblePages()

  if (totalPages <= 1) return null

  return (
    <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200/50 dark:border-gray-700/50 bg-gray-50/30 dark:bg-gray-700/30">
      {/* Mobile Layout */}
      <div className="flex sm:hidden flex-col space-y-3">
        {/* Page Info */}
        <div className="text-center">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {currentLang === 'zh' 
              ? `第 ${currentPage} 頁，共 ${totalPages} 頁`
              : `Page ${currentPage} of ${totalPages}`
            }
          </span>
        </div>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={goToPreviousPage}
            disabled={!canGoPrevious}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              canGoPrevious
                ? 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 shadow-sm'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-700 cursor-not-allowed'
            }`}
          >
            <SafeIcon icon={FiChevronLeft} className="w-4 h-4" />
            <span>{currentLang === 'zh' ? '上一頁' : 'Prev'}</span>
          </motion.button>

          {/* Page Numbers - Mobile */}
          <div className="flex items-center gap-1">
            {mobileVisiblePages.map((page) => (
              <motion.button
                key={page}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => goToPage(page)}
                className={`w-8 h-8 rounded-lg text-sm font-medium transition-all duration-200 ${
                  currentPage === page
                    ? 'bg-purple-500 text-white shadow-md'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                {page}
              </motion.button>
            ))}
          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={goToNextPage}
            disabled={!canGoNext}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              canGoNext
                ? 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 shadow-sm'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-700 cursor-not-allowed'
            }`}
          >
            <span>{currentLang === 'zh' ? '下一頁' : 'Next'}</span>
            <SafeIcon icon={FiChevronRight} className="w-4 h-4" />
          </motion.button>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden sm:flex items-center justify-between">
        {/* Items per page selector */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-700 dark:text-gray-300">
            {translations.pagination?.itemsPerPage?.[currentLang] || (currentLang === 'zh' ? '每頁顯示' : 'Items per page')}
          </label>
          <select
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>

        {/* Page navigation */}
        <div className="flex items-center gap-2">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={goToPreviousPage}
            disabled={!canGoPrevious}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              canGoPrevious
                ? 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 shadow-sm'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-700 cursor-not-allowed'
            }`}
          >
            <SafeIcon icon={FiChevronLeft} className="w-4 h-4" />
            <span>{translations.pagination?.previous?.[currentLang] || (currentLang === 'zh' ? '上一頁' : 'Previous')}</span>
          </motion.button>

          {/* Page numbers */}
          <div className="flex items-center gap-1">
            {visiblePages.map((page, index) => (
              page === '...' ? (
                <div key={`dots-${index}`} className="px-2 py-2">
                  <SafeIcon icon={FiMoreHorizontal} className="w-4 h-4 text-gray-400" />
                </div>
              ) : (
                <motion.button
                  key={page}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => goToPage(page)}
                  className={`w-10 h-10 rounded-lg text-sm font-medium transition-all duration-200 ${
                    currentPage === page
                      ? 'bg-purple-500 text-white shadow-md'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                >
                  {page}
                </motion.button>
              )
            ))}
          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={goToNextPage}
            disabled={!canGoNext}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              canGoNext
                ? 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 shadow-sm'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-700 cursor-not-allowed'
            }`}
          >
            <span>{translations.pagination?.next?.[currentLang] || (currentLang === 'zh' ? '下一頁' : 'Next')}</span>
            <SafeIcon icon={FiChevronRight} className="w-4 h-4" />
          </motion.button>
        </div>

        {/* Page info */}
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {translations.pagination?.page?.[currentLang]?.replace('{current}', currentPage).replace('{total}', totalPages) ||
            (currentLang === 'zh' ? `第 ${currentPage} 頁，共 ${totalPages} 頁` : `Page ${currentPage} of ${totalPages}`)
          }
        </div>
      </div>
    </div>
  )
}

export default Pagination